import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Divider,
  Link,
  ClickAwayListener,
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingCart,
  People,
  Inventory,
  AccountCircle,
  Receipt,
  History,
  TrendingUp,
  AccessTime,
  CheckCircle,
  Cancel,
  HourglassEmpty,
} from '@mui/icons-material';
import api from '../services/api';

const RECENT_SEARCHES_KEY = 'paceestate_recent_searches';
const MAX_RECENT_SEARCHES = 5;
const MAX_RESULTS_PER_TYPE = 5;
const DEBOUNCE_DELAY = 300;

const SEARCH_TYPES = {
  sales: {
    icon: <ShoppingCart fontSize="small" />,
    color: '#1976d2',
    label: 'Sales',
    endpoint: '/sales',
    getTitle: (item) => item.title || item.client_name,
    getSubtitle: (item) => `${item.status} - ${new Date(item.sale_date).toLocaleDateString()}`,
    getPath: (item) => `/sales/${item.id}`,
    getStatus: (item) => item.status,
  },
  leads: {
    icon: <TrendingUp fontSize="small" />,
    color: '#f57c00',
    label: 'Leads',
    endpoint: '/leads',
    getTitle: (item) => item.name,
    getSubtitle: (item) => `${item.source} - ${item.status}`,
    getPath: (item) => `/leads/${item.id}`,
    getStatus: (item) => item.status,
  },
  inventory: {
    icon: <Inventory fontSize="small" />,
    color: '#388e3c',
    label: 'Inventory',
    endpoint: '/inventory/items',
    getTitle: (item) => item.name,
    getSubtitle: (item) => `${item.category} - $${item.price}`,
    getPath: (item) => `/inventory/${item.id}`,
    getStatus: (item) => item.status,
  },
  clients: {
    icon: <AccountCircle fontSize="small" />,
    color: '#7b1fa2',
    label: 'Clients',
    endpoint: '/clients',
    getTitle: (item) => item.name || `${item.first_name} ${item.last_name}`,
    getSubtitle: (item) => item.email || item.phone,
    getPath: (item) => `/clients/${item.id}`,
    getStatus: () => null,
  },
  invoices: {
    icon: <Receipt fontSize="small" />,
    color: '#c62828',
    label: 'Invoices',
    endpoint: '/invoices',
    getTitle: (item) => `Invoice #${item.invoice_number}`,
    getSubtitle: (item) => `$${item.total_amount} - ${item.status}`,
    getPath: (item) => `/invoices/${item.id}`,
    getStatus: (item) => item.status,
  },
};

const STATUS_ICONS = {
  pending: <HourglassEmpty fontSize="small" />,
  scheduled: <AccessTime fontSize="small" />,
  completed: <CheckCircle fontSize="small" />,
  cancelled: <Cancel fontSize="small" />,
  closed: <CheckCircle fontSize="small" />,
};

const GlobalSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState({});
  const [recentSearches, setRecentSearches] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch (error) {
        console.error('Failed to load recent searches:', error);
      }
    }
  }, []);

  // Save recent search
  const saveRecentSearch = useCallback((query) => {
    if (!query || query.trim().length < 2) return;

    setRecentSearches((prev) => {
      const updated = [query, ...prev.filter((q) => q !== query)].slice(0, MAX_RECENT_SEARCHES);
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Perform search across all endpoints
  const performSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setResults({});
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const searchPromises = Object.entries(SEARCH_TYPES).map(async ([type, config]) => {
        try {
          const response = await api.get(`${config.endpoint}?search=${encodeURIComponent(query)}`);
          return {
            type,
            data: Array.isArray(response.data) ? response.data : response.data.results || [],
          };
        } catch (error) {
          console.error(`Search failed for ${type}:`, error);
          return { type, data: [] };
        }
      });

      const searchResults = await Promise.all(searchPromises);

      const resultsMap = {};
      searchResults.forEach(({ type, data }) => {
        if (data.length > 0) {
          resultsMap[type] = data.slice(0, MAX_RESULTS_PER_TYPE);
        }
      });

      setResults(resultsMap);
    } catch (error) {
      console.error('Search error:', error);
      setResults({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search handler
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (searchQuery.trim().length >= 2) {
      debounceTimerRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, DEBOUNCE_DELAY);
    } else {
      setResults({});
      setIsLoading(false);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, performSearch]);

  // Keyboard shortcut handler (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        searchInputRef.current?.focus();
        setIsOpen(true);
      }

      if (event.key === 'Escape') {
        setIsOpen(false);
        searchInputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (event) => {
    const totalResults = Object.values(results).reduce((sum, items) => sum + items.length, 0);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev < totalResults - 1 ? prev + 1 : prev));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (event.key === 'Enter' && selectedIndex >= 0) {
      event.preventDefault();
      const allResults = Object.entries(results).flatMap(([type, items]) =>
        items.map((item) => ({ type, item }))
      );
      const selected = allResults[selectedIndex];
      if (selected) {
        handleResultClick(selected.type, selected.item);
      }
    }
  };

  const handleResultClick = (type, item) => {
    const config = SEARCH_TYPES[type];
    saveRecentSearch(searchQuery);
    setIsOpen(false);
    setSearchQuery('');
    navigate(config.getPath(item));
  };

  const handleRecentSearchClick = (query) => {
    setSearchQuery(query);
    performSearch(query);
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleInputChange = (event) => {
    setSearchQuery(event.target.value);
    setSelectedIndex(-1);
  };

  const totalResults = Object.values(results).reduce((sum, items) => sum + items.length, 0);
  const hasResults = totalResults > 0;
  const showRecentSearches = isOpen && !searchQuery && recentSearches.length > 0;
  const showNoResults = isOpen && searchQuery.trim().length >= 2 && !isLoading && !hasResults;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative', width: { xs: '100%', sm: 400 }, mr: 2 }}>
        <TextField
          ref={searchInputRef}
          fullWidth
          size="small"
          placeholder="Search sales, leads, inventory..."
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘K' : 'Ctrl+K'}
                </Typography>
              </InputAdornment>
            ),
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.03)',
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
              },
            },
          }}
        />

        {isOpen && (
          <Paper
            elevation={8}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 1,
              maxHeight: '70vh',
              overflowY: 'auto',
              zIndex: 1300,
            }}
          >
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {showRecentSearches && (
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <History fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    RECENT SEARCHES
                  </Typography>
                </Box>
                <List dense>
                  {recentSearches.map((query, index) => (
                    <ListItem key={index} disablePadding>
                      <ListItemButton onClick={() => handleRecentSearchClick(query)}>
                        <ListItemText primary={query} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {showNoResults && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No results found for "{searchQuery}"
                </Typography>
              </Box>
            )}

            {hasResults && !isLoading && (
              <Box>
                {Object.entries(results).map(([type, items], groupIndex) => {
                  const config = SEARCH_TYPES[type];
                  let currentIndex = -1;

                  // Calculate the starting index for this group
                  const previousGroups = Object.entries(results).slice(0, groupIndex);
                  const previousItemsCount = previousGroups.reduce(
                    (sum, [, groupItems]) => sum + groupItems.length,
                    0
                  );

                  return (
                    <Box key={type}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 2,
                          py: 1,
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ color: config.color, mr: 1 }}>{config.icon}</Box>
                          <Typography variant="caption" fontWeight={600} color="text.secondary">
                            {config.label.toUpperCase()}
                          </Typography>
                        </Box>
                        <Link
                          component="button"
                          variant="caption"
                          onClick={() => {
                            navigate(`${config.endpoint.replace('/api', '')}?search=${searchQuery}`);
                            setIsOpen(false);
                            setSearchQuery('');
                          }}
                          sx={{ textDecoration: 'none' }}
                        >
                          View all
                        </Link>
                      </Box>

                      <List dense>
                        {items.map((item, index) => {
                          currentIndex = previousItemsCount + index;
                          const isSelected = currentIndex === selectedIndex;

                          return (
                            <ListItem key={item.id} disablePadding>
                              <ListItemButton
                                selected={isSelected}
                                onClick={() => handleResultClick(type, item)}
                                sx={{
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                  },
                                }}
                              >
                                <ListItemIcon sx={{ color: config.color, minWidth: 36 }}>
                                  {config.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={config.getTitle(item)}
                                  secondary={config.getSubtitle(item)}
                                  primaryTypographyProps={{
                                    variant: 'body2',
                                    noWrap: true,
                                  }}
                                  secondaryTypographyProps={{
                                    variant: 'caption',
                                    noWrap: true,
                                  }}
                                />
                                {config.getStatus(item) && (
                                  <Chip
                                    size="small"
                                    label={config.getStatus(item)}
                                    icon={STATUS_ICONS[config.getStatus(item)?.toLowerCase()]}
                                    sx={{
                                      height: 24,
                                      fontSize: '0.7rem',
                                      textTransform: 'capitalize',
                                    }}
                                  />
                                )}
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>

                      {groupIndex < Object.keys(results).length - 1 && <Divider />}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;
